// engine-exception: portal commerce — quotes (shared customer QuoteBody with portal endpoints), issued invoices with a printable statement, agreements + prepaid balances; customer documents, not staff resource screens
import { useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../engine/api/context';
import { Icon } from '../../engine/design/icons';
import { Badge, Banner, Chip, EmptyState, Loading, Money, PageHeader } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { useToast } from '../../engine/design/primitives/Toast';
import { ApiError } from '../../engine/api/client';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { formatDate, formatQty } from '../../lib/format';
import { QuoteBody, quoteLoadError, type AcceptBody, type PublicView } from '../../public/quote/QuoteView';
import { usePortal } from '../PortalContext';
import { portalKeys, usePortalItem, usePortalList } from '../hooks';
import type { ContractRow, InvoiceDetail, InvoiceRow, QuoteRow } from '../types';
import { BackLink, ListState, LoadMore, openDocument, StatusPill, useViewPref, ViewToggle } from '../ui';

// ── Quotes ────────────────────────────────────────────────────────────────────
export function QuotesPage() {
  const { can } = usePortal();
  const list = usePortalList<QuoteRow>('quotes', {}, { enabled: can('quotes.view') });
  if (!can('quotes.view')) return <EmptyState title="Quotes are not available to your portal role" icon="lock" />;
  return (
    <div>
      <PageHeader title="Quotes" subtitle="Proposals sent to you — open one to review, accept or decline" />
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: 'No quotes yet', icon: 'quote' }}>
        <div className="pt-cards">
          {list.rows.map((q) => {
            const open = q.status === 'sent' || q.status === 'viewed';
            return (
              <Link key={q.id} to={`/portal/quotes/${q.id}`} className="pt-card">
                <span className="ref">{q.ref}{q.revision > 1 ? ` · rev ${q.revision}` : ''}</span>
                <span className="title">{q.title}</span>
                <span className="meta">{q.sent_at && <span>Sent {formatDate(q.sent_at)}</span>}{q.expires_at && open && <span>Valid until {formatDate(q.expires_at)}</span>}{q.accepted_at && <span>Accepted {formatDate(q.accepted_at)}</span>}</span>
                <span className="side"><StatusPill status={q.status} />{open && can('quotes.accept') && <Badge tone="accent" dot={false}>Action needed</Badge>}</span>
              </Link>
            );
          })}
        </div>
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
      </ListState>
    </div>
  );
}

export function QuoteDetailPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const qc = useQueryClient();
  const view = usePortalItem<PublicView>('quotes', id);
  const endpoints = useMemo(
    () => ({
      accept: (body: AcceptBody) => api.resource('quotes').action<PublicView>('accept', { id, body }),
      decline: (body: { reason: string | null }) => api.resource('quotes').action<PublicView>('decline', { id, body }),
      onPrint: () => void openDocument(api, `/v1/quotes/${encodeURIComponent(id)}/print`).catch(() => undefined),
    }),
    [api, id],
  );
  if (view.isLoading) return <Loading label="Loading your quote…" />;
  if (view.error || !view.data) {
    const { title, hint, icon } = view.error ? quoteLoadError(view.error) : { title: 'Quote not found', hint: undefined, icon: 'alert-circle' as const };
    return <EmptyState title={title} hint={hint} icon={icon} action={<Link className="hf-btn" to="/portal/quotes">Back to quotes</Link>} />;
  }
  return (
    <div>
      <BackLink to="/portal/quotes">Quotes</BackLink>
      <QuoteBody
        endpoints={endpoints}
        data={view.data}
        onChange={(v) => {
          qc.setQueryData(portalKeys.item('quotes', id), v);
          void qc.invalidateQueries({ queryKey: ['portal', 'list', 'quotes'] });
        }}
      />
    </div>
  );
}

// ── Invoices ──────────────────────────────────────────────────────────────────
function outstanding(r: InvoiceRow): bigint {
  return BigInt(r.total_minor) - BigInt(r.paid_minor);
}

/** Opens an invoice's document (print view, or the connected system's PDF) with the session. */
function useOpenInvoice() {
  const api = useApi();
  const toast = useToast();
  return (id: string) =>
    void openDocument(api, `/v1/invoices/${encodeURIComponent(id)}/print`).catch((e: unknown) =>
      toast({ kind: 'error', title: 'Could not open the invoice', detail: e instanceof ApiError ? e.detail : String(e) }),
    );
}

/** Outstanding per currency, over unpaid, non-void invoices (credit notes reduce it). */
export function balances(rows: InvoiceRow[]): { currency: string; minor: bigint; count: number; overdue: number }[] {
  const by = new Map<string, { currency: string; minor: bigint; count: number; overdue: number }>();
  for (const r of rows) {
    if (r.status === 'void') continue;
    const o = outstanding(r);
    if (o === 0n) continue;
    const b = by.get(r.currency) ?? { currency: r.currency, minor: 0n, count: 0, overdue: 0 };
    b.minor += o;
    b.count += o > 0n ? 1 : 0;
    if (o > 0n && r.due_date && new Date(r.due_date).getTime() < Date.now()) b.overdue += 1;
    by.set(r.currency, b);
  }
  return [...by.values()].filter((b) => b.minor !== 0n);
}

export function InvoicesPage() {
  const { can } = usePortal();
  const navigate = useNavigate();
  const openInvoice = useOpenInvoice();
  const [view, setView] = useViewPref('invoices');
  // Settled invoices are history: hidden until asked for (the choice rides in the URL).
  const [sp, setSp] = useSearchParams();
  const showPaid = sp.get('paid') === '1';
  const list = usePortalList<InvoiceRow>('invoices', {}, { enabled: can('invoices.view') });
  const columns = useMemo<GridColumn<InvoiceRow>[]>(
    () => [
      { id: 'number', header: 'Invoice', width: 140, cell: (r) => <button type="button" className="hf-link hf-mono" onClick={(e) => { e.stopPropagation(); openInvoice(r.id); }} title="Open the invoice document">{r.number ?? '—'}</button> },
      { id: 'issue', header: 'Issued', width: 120, accessor: (r) => formatDate(r.issue_date) },
      { id: 'due', header: 'Due', width: 120, accessor: (r) => formatDate(r.due_date) },
      { id: 'status', header: 'Status', width: 120, cell: (r) => <InvoiceStatus r={r} /> },
      { id: 'total', header: 'Total', width: 130, align: 'right', cell: (r) => <Money minor={r.total_minor} currency={r.currency} /> },
      { id: 'outstanding', header: 'Outstanding', width: 130, align: 'right', cell: (r) => <Money minor={outstanding(r).toString()} currency={r.currency} color /> },
      { id: 'pay', header: '', width: 110, cell: (r) => (r.pay_url && outstanding(r) > 0n ? <PayLink url={r.pay_url} /> : null) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openInvoice is stable in behaviour (api + toast)
    [],
  );
  if (!can('invoices.view')) return <EmptyState title="Invoices are not available to your portal role" icon="lock" />;
  const owed = balances(list.rows);
  const shown = showPaid ? list.rows : list.rows.filter((r) => r.status !== 'void' && outstanding(r) !== 0n);
  const hidden = list.rows.length - shown.length;
  const togglePaid = () => setSp((p) => { const n = new URLSearchParams(p); if (showPaid) n.delete('paid'); else n.set('paid', '1'); return n; }, { replace: true });
  return (
    <div>
      <PageHeader title="Invoices" subtitle={owed.length > 0 ? 'What you owe, oldest first to settle' : 'All settled — thank you'} actions={<ViewToggle value={view} onChange={setView} />} />
      {owed.length > 0 && (
        <div className="pt-balance" role="status" aria-label="Balance due">
          {owed.map((b) => (
            <div key={b.currency}>
              <span className="hf-caps">Balance due</span>
              <strong><Money minor={b.minor.toString()} currency={b.currency} /></strong>
              <span className="hf-muted">{b.count} invoice{b.count === 1 ? '' : 's'}{b.overdue > 0 ? ` · ${b.overdue} overdue` : ''}</span>
            </div>
          ))}
        </div>
      )}
      <div className="hf-row" style={{ marginBottom: 'var(--s-3)' }}>
        <Chip active={showPaid} aria-pressed={showPaid} onClick={togglePaid}>{showPaid ? 'Hide paid' : `Show paid${hidden > 0 ? ` (${hidden})` : ''}`}</Chip>
      </div>
      <ListState loading={list.isLoading} error={list.error} count={shown.length} empty={{ title: list.rows.length ? 'Nothing outstanding' : 'No invoices yet', icon: 'receipt' }}>
        {view === 'table' ? (
          <DataGrid<InvoiceRow> columns={columns} rows={shown} keyOf={(r) => r.id} onRowClick={(r) => navigate(`/portal/invoices/${r.id}`)} hasMore={list.hasMore} loadMore={list.loadMore} loadingMore={list.loadingMore} height="auto" />
        ) : (
          <>
            <div className="pt-cards">
              {shown.map((r) => (
                <Link key={r.id} to={`/portal/invoices/${r.id}`} className="pt-card">
                  <span className="ref">{r.number ?? 'Invoice'}{r.kind === 'credit_note' ? ' · credit note' : ''}</span>
                  <span className="title">{r.billing_party_name ?? 'Invoice'}</span>
                  <span className="meta"><span>Issued {formatDate(r.issue_date)}</span>{r.due_date && <span>Due {formatDate(r.due_date)}</span>}</span>
                  <span className="side"><span className="amount"><Money minor={r.total_minor} currency={r.currency} /></span><InvoiceStatus r={r} /></span>
                </Link>
              ))}
            </div>
            <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
          </>
        )}
      </ListState>
    </div>
  );
}

/** Pay online (the provider's payment page, opened outside the portal). */
function PayLink({ url }: { url: string }) {
  if (!/^https:\/\//.test(url)) return null;
  return (
    <a className="hf-btn primary sm" href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
      <Icon name="credit-card" size={14} /> Pay online
    </a>
  );
}

function InvoiceStatus({ r }: { r: InvoiceRow }) {
  const o = outstanding(r);
  if (r.status === 'void') return <Badge tone="muted">Void</Badge>;
  if (o <= 0n) return <Badge tone="pos">Paid</Badge>;
  const overdue = r.due_date ? new Date(r.due_date).getTime() < Date.now() : false;
  return <Badge tone={overdue ? 'neg' : 'warn'}>{overdue ? 'Overdue' : BigInt(r.paid_minor) > 0n ? 'Part paid' : 'Unpaid'}</Badge>;
}

export function InvoicePage() {
  const { id = '' } = useParams();
  const openInvoice = useOpenInvoice();
  const { me } = usePortal();
  const inv = usePortalItem<InvoiceDetail>('invoices', id);
  if (inv.isLoading) return <Loading />;
  if (inv.error || !inv.data) return <EmptyState title={inv.error?.status === 404 ? 'Invoice not found' : 'Could not load this invoice'} hint={inv.error?.detail} icon="receipt" action={<Link className="hf-btn" to="/portal/invoices">Back to invoices</Link>} />;
  const d = inv.data;
  const o = outstanding(d);
  return (
    <div className="pt-main narrow" style={{ padding: 0 }}>
      <BackLink to="/portal/invoices">Invoices</BackLink>
      <PageHeader
        eyebrow={d.kind === 'credit_note' ? 'Credit note' : 'Invoice'}
        title={d.number ?? 'Invoice'}
        badge={<span style={{ marginLeft: 8 }}><InvoiceStatus r={d} /></span>}
        actions={
          <span className="hf-row hf-noprint">
            {d.pay_url && o > 0n && <PayLink url={d.pay_url} />}
            <Button variant="outline" icon="file-text" onClick={() => openInvoice(id)}>{d.lines === null ? 'Open PDF' : 'Print / PDF'}</Button>
          </span>
        }
      />
      {o > 0n && d.status !== 'void' && (
        <Banner tone={d.due_date && new Date(d.due_date).getTime() < Date.now() ? 'error' : 'warn'}>
          <Money minor={o.toString()} currency={d.currency} /> outstanding{d.due_date ? ` — due ${formatDate(d.due_date)}` : ''}.
        </Banner>
      )}
      <div className="pt-doc">
        <div className="hf-between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
          <div><div className="hf-caps">From</div><div>{d.tenant_name ?? me.tenant.name}</div></div>
          <div><div className="hf-caps">Billed to</div><div>{d.billing_party_name ?? me.client?.name}</div></div>
          <div><div className="hf-caps">Issued</div><div>{formatDate(d.issue_date)}</div></div>
          <div><div className="hf-caps">Due</div><div>{formatDate(d.due_date)}</div></div>
        </div>
        {d.lines === null && <p className="hf-muted">The itemised invoice is in its PDF.</p>}
        {d.lines !== null && <table>
          <thead><tr><th>Description</th><th className="num">Qty</th><th className="num">Unit</th><th className="num">Net</th></tr></thead>
          <tbody>
            {(d.lines ?? []).map((l) => (
              <tr key={l.id}><td>{l.description}</td><td className="num">{formatQty(l.quantity)}</td><td className="num"><Money minor={l.unit_price_minor} currency={d.currency} /></td><td className="num"><Money minor={l.net_minor} currency={d.currency} /></td></tr>
            ))}
          </tbody>
        </table>}
        <dl className="hf-kv totals">
          <div><dt>Subtotal</dt><dd className="num" style={{ textAlign: 'right' }}><Money minor={d.subtotal_minor} currency={d.currency} /></dd></div>
          <div><dt>Tax</dt><dd className="num" style={{ textAlign: 'right' }}><Money minor={d.tax_minor} currency={d.currency} /></dd></div>
          <div><dt><strong>Total</strong></dt><dd className="num" style={{ textAlign: 'right' }}><strong><Money minor={d.total_minor} currency={d.currency} /></strong></dd></div>
          <div><dt>Paid</dt><dd className="num" style={{ textAlign: 'right' }}><Money minor={d.paid_minor} currency={d.currency} /></dd></div>
          <div><dt><strong>Outstanding</strong></dt><dd className="num" style={{ textAlign: 'right' }}><strong><Money minor={o.toString()} currency={d.currency} color /></strong></dd></div>
        </dl>
        {(d.payments ?? []).length > 0 && (
          <>
            <div className="hf-caps" style={{ marginTop: 'var(--s-4)' }}>Payments</div>
            <table>
              <tbody>
                {d.payments!.map((p) => (
                  <tr key={p.id}><td>{formatDate(p.paid_at)}</td><td>{p.method ?? '—'}{p.reference ? ` · ${p.reference}` : ''}</td><td className="num"><Money minor={p.amount_minor} currency={p.currency} /></td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// ── Agreements (contracts + prepaid balances) ─────────────────────────────────
export function ContractsPage() {
  const { can } = usePortal();
  const list = usePortalList<ContractRow>('contracts', {}, { enabled: can('contracts.view'), limit: 100 });
  if (!can('contracts.view')) return <EmptyState title="Agreements are not available to your portal role" icon="lock" />;
  return (
    <div>
      <PageHeader title="Agreements" subtitle="Your contracts and prepaid balances" />
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: 'No agreements on file', icon: 'file-check' }}>
        <div className="pt-cards">
          {list.rows.map((c) => (
            <div key={c.id} className="pt-card" style={{ cursor: 'default' }}>
              <span className="ref">{c.ref}</span>
              <span className="title">{c.billing_party_name ?? 'Agreement'} · {c.cycle.replace(/_/g, ' ')}</span>
              <span className="meta">
                {c.start_date && <span>From {formatDate(c.start_date)}</span>}
                {c.end_date && <span>to {formatDate(c.end_date)}</span>}
                {c.next_invoice_date && <span>Next invoice {formatDate(c.next_invoice_date)}</span>}
              </span>
              <span className="side"><span className="amount"><Money minor={c.period_charge_minor} currency={c.currency} /></span><StatusPill status={c.status} /></span>
              {(c.prepay_accounts ?? []).length > 0 && (
                <div style={{ gridColumn: '1 / -1', marginTop: 6 }}>
                  <div className="hf-caps">Prepaid</div>
                  {c.prepay_accounts!.map((pa) => (
                    <div key={pa.id} className="hf-between" style={{ fontSize: 'var(--t-13)', padding: '4px 0', borderTop: '1px solid var(--rule)' }}>
                      <span>{pa.unit === 'money' ? 'Prepaid funds' : `Prepaid ${pa.unit}`}</span>
                      <span className="hf-num">
                        {pa.balance ? (
                          pa.unit === 'money' ? <Money minor={pa.balance.remaining_qty} currency={pa.currency} color /> : `${formatQty(pa.balance.remaining_qty)} ${pa.unit} left of ${formatQty(pa.balance.purchased_qty)}`
                        ) : '—'}
                        {pa.balance?.over_drawn && <Badge tone="neg">overdrawn</Badge>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </ListState>
    </div>
  );
}
